import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../config/httpConfig";
import {
  applyColumnPreference,
  getDefaultColumnKeys,
  mergeVisibleOrderIntoPreference,
  orderDropdownOptions,
  splitTableColumns,
  withHeaderColumnDrag,
} from "../helpers/tableColumnPreference";

/**
 * Load / save / reset per-user table column preferences for a screen.
 * @param {string} screenKey e.g. "admin.deals.DCT1000001"
 * @param {Array} columns full Ant Design column defs (including Actions)
 * @param {{ defaultColumnKeys?: string[] }} options preferred default data keys (excl. Actions)
 */
const useTableColumnPreference = (screenKey, columns = [], options = {}) => {
  const preferredDefaultKeys = options.defaultColumnKeys;
  const [preferenceKeys, setPreferenceKeys] = useState(null);
  const [loading, setLoading] = useState(Boolean(screenKey));
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef(null);
  const screenKeyRef = useRef(screenKey);

  const { dataColumns, actionsColumn } = useMemo(
    () => splitTableColumns(columns),
    [columns],
  );

  const defaultKeys = useMemo(
    () => getDefaultColumnKeys(columns, preferredDefaultKeys),
    [columns, preferredDefaultKeys],
  );

  const availableOptions = useMemo(
    () =>
      dataColumns.map((column) => ({
        key: column.key,
        label:
          typeof column.title === "string"
            ? column.title
            : column.key,
      })),
    [dataColumns],
  );

  const availableKeys = useMemo(
    () => availableOptions.map((option) => option.key),
    [availableOptions],
  );

  useEffect(() => {
    screenKeyRef.current = screenKey;
  }, [screenKey]);

  useEffect(() => {
    if (!screenKey) {
      setPreferenceKeys(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await axios.get("/api/admin/tableColumnPreference", {
          params: { screenKey },
        });
        if (cancelled || screenKeyRef.current !== screenKey) return;
        const keys = response?.response?.columnKeys;
        setPreferenceKeys(Array.isArray(keys) && keys.length ? keys : null);
      } catch {
        if (!cancelled) setPreferenceKeys(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [screenKey]);

  const persistKeys = useCallback((nextKeys) => {
    if (!screenKey) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.post("/api/admin/tableColumnPreference", {
          screenKey,
          columnKeys: nextKeys,
        });
      } catch {
        // Keep local preference; user can retry by changing again
      } finally {
        setSaving(false);
      }
    }, 300);
  }, [screenKey]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const effectivePreferenceKeys = Array.isArray(preferenceKeys)
    ? preferenceKeys
    : defaultKeys;

  const visibleOrderedKeys = useMemo(() => {
    const available = new Set(availableKeys);
    if (Array.isArray(preferenceKeys)) {
      return preferenceKeys.filter((key) => available.has(key));
    }
    const fallback = defaultKeys.filter((key) => available.has(key));
    return fallback.length ? fallback : availableKeys.slice(0, 3);
  }, [availableKeys, defaultKeys, preferenceKeys]);

  const dropdownOptions = useMemo(
    () => orderDropdownOptions(availableOptions, visibleOrderedKeys),
    [availableOptions, visibleOrderedKeys],
  );

  const updatePreference = useCallback(
    (nextVisibleOrderedKeys) => {
      const merged = mergeVisibleOrderIntoPreference(
        effectivePreferenceKeys,
        nextVisibleOrderedKeys,
        availableKeys,
      );
      setPreferenceKeys(merged);
      persistKeys(merged);
    },
    [availableKeys, effectivePreferenceKeys, persistKeys],
  );

  const reorderVisibleColumns = useCallback(
    (fromIndex, toIndex) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= visibleOrderedKeys.length ||
        toIndex >= visibleOrderedKeys.length
      ) {
        return;
      }
      const next = [...visibleOrderedKeys];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      updatePreference(next);
    },
    [updatePreference, visibleOrderedKeys],
  );

  const reorderVisibleColumnsByKey = useCallback(
    (fromKey, toKey) => {
      const fromIndex = visibleOrderedKeys.indexOf(fromKey);
      const toIndex = visibleOrderedKeys.indexOf(toKey);
      if (fromIndex < 0 || toIndex < 0) return;
      reorderVisibleColumns(fromIndex, toIndex);
    },
    [reorderVisibleColumns, visibleOrderedKeys],
  );

  const tableColumns = useMemo(() => {
    const applied = applyColumnPreference(
      columns,
      effectivePreferenceKeys,
      preferredDefaultKeys,
    );
    return withHeaderColumnDrag(applied, reorderVisibleColumnsByKey);
  }, [
    columns,
    effectivePreferenceKeys,
    preferredDefaultKeys,
    reorderVisibleColumnsByKey,
  ]);

  const toggleColumn = useCallback(
    (columnKey) => {
      if (!columnKey) return;
      const isVisible = visibleOrderedKeys.includes(columnKey);
      if (isVisible) {
        if (visibleOrderedKeys.length <= 1) return;
        updatePreference(visibleOrderedKeys.filter((key) => key !== columnKey));
        return;
      }
      updatePreference([...visibleOrderedKeys, columnKey]);
    },
    [updatePreference, visibleOrderedKeys],
  );

  const toggleAllColumns = useCallback(
    (selectAll) => {
      if (selectAll) {
        const visibleSet = new Set(visibleOrderedKeys);
        const remaining = availableKeys.filter((key) => !visibleSet.has(key));
        const allKeys = [...visibleOrderedKeys, ...remaining];
        updatePreference(allKeys);
      } else {
        updatePreference([]);
      }
    },
    [availableKeys, updatePreference, visibleOrderedKeys],
  );

  const resetToDefault = useCallback(async () => {
    if (!screenKey) {
      setPreferenceKeys(null);
      return;
    }
    setSaving(true);
    try {
      await axios.post("/api/admin/tableColumnPreference/reset", { screenKey });
      setPreferenceKeys(null);
    } catch {
      setPreferenceKeys(null);
    } finally {
      setSaving(false);
    }
  }, [screenKey]);

  return {
    tableColumns,
    availableOptions: dropdownOptions,
    visibleOrderedKeys,
    actionsColumn,
    loading,
    saving,
    isCustomized: Array.isArray(preferenceKeys),
    toggleColumn,
    toggleAllColumns,
    reorderVisibleColumns,
    resetToDefault,
  };
};

export default useTableColumnPreference;
